import { useEffect, useState } from "react";
import { Tab, Tabs } from "react-bootstrap";
import styles from "./orderData.module.css";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { orderApi, useGetOrdersQuery } from "../../features/api/order/orderApi";
import dateFormatter from "../../utils/dateFormatter";
import { OrderFilter } from "./OrderFilter";
import { PlaceOrder } from "./placeOrder/PlaceOrder";
import { DeliveryStatus } from "../../app/constant/DeliveryStatus";
import { useFullName } from "../../customHooks/useFullName";
import { setFilter } from "../../features/api/order/orderFilterSlice";
import { useIsSalesExecutive } from "../../customHooks/roleChecker/useIsSalesExecutive";
export const OrderData = () => {
  const [key, setKey] = useState("Order List");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userFullName = useFullName();
  const isSalesExecutive = useIsSalesExecutive();

  // Pagination States
  const [pageNo, setPageNo] = useState(0);
  const [pageSize, setPageSize] = useState(15);
  const [totalPages, setTotalPages] = useState(null);
  const [orders, setOrders] = useState([]);
  const [totalDisplayed, setTotalDisplayed] = useState(0);

  const orderFilter = useSelector((state) => state.orderFilter);

  // Fetching data from API
  const { data, isLoading, isError } = useGetOrdersQuery({
    page: pageNo,
    size: pageSize,
    ...orderFilter,
  });

  // Function to fetch next page of products
  const getNextPage = (page) => {
    dispatch(
      orderApi.endpoints.getOrders.initiate({
        page: pageNo,
        size: pageSize,
        ...orderFilter,
      })
    );
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 0 && newPage < totalPages) {
      setPageNo(newPage);
      getNextPage(newPage);
    }
  };

  // Handle page size change
  const handlePageSizeChange = (event) => {
    const newSize = Number(event.target.value);
    setPageSize(newSize);
    setPageNo(0); // Reset to first page when page size changes
  };

  // When data is fetched, update the products state
  useEffect(() => {
    if (data?.content) {
      setOrders(data.content);
      setTotalPages(data.totalPages);
      calculateTotalDisplayed(data.content);
    }
  }, [data]);
  useEffect(() => {
    setPageNo(0);
  }, [orderFilter]);

  const calculateTotalDisplayed = (data) => {
    let total = 0;
    data?.forEach((order) => {
      if (order.orderType === "INVOICE") {
        total += order.totalTTC - order.paid;
      } else if (order.orderType === "DELIVERY") {
        total += order.total - order.paid;
      }
    });
    setTotalDisplayed(total);
  };
  useEffect(() => {
    if (isSalesExecutive) {
      dispatch(
        setFilter({
          property: "createdBy",
          value: userFullName,
        })
      );
    }
  }, [isSalesExecutive, userFullName]);
  return (
    <div className="container pt-5">
      <Tabs
        id="controlled-tab"
        activeKey={key}
        onSelect={(k) => setKey(k)}
        className="mb-3"
      >
        <Tab eventKey="Order List" title="Order List">
          <OrderFilter />
          <div className={styles.tableContainer}>
            {isLoading ? (
              <p>Loading...</p>
            ) : isError ? (
              <p>Error fetching data</p>
            ) : (
              <>
                <table className={styles.customTable}>
                  <thead>
                    <tr>
                      <th className="text-center">No</th>
                      <th className="text-center">Company</th>
                      <th className="text-center">Date</th>
                      <th className="text-center">Total (€)</th>
                      <th className="text-center">Paid (€)</th>
                      <th className="text-center">Due (€)</th>
                      <th className="text-center">Delivery status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.length > 0 ? (
                      <>
                        {orders.map((order, idx) => (
                          <tr
                            key={idx}
                            onClick={() => navigate(`/order/${order.id}`)}
                          >
                            <td className="text-center">
                              {(order.reference.length === 15
                                ? order.reference.substring(0, 4)
                                : order.reference.substring(0, 3)) +
                                order.reference.slice(-4)}
                            </td>
                            <td className={`${styles.capitalize} text-center`}>
                              {order.companyName}
                            </td>
                            <td className="text-center">
                              {dateFormatter(order.createdAt)}
                            </td>
                            <td className="text-center">
                              {order.orderType === "INVOICE" ||
                              order.orderType === "REFUND"
                                ? order.totalTTC.toFixed(2)
                                : order.total.toFixed(2)}
                            </td>
                            <td className="text-center">
                              {order.paid.toFixed(2)}
                            </td>
                            <td
                              className={`${
                                order.orderType === "DRAFT"
                                  ? styles.naIndication
                                  : order.paymentStatus === "PAID"
                                  ? styles.paidIndication
                                  : styles.dueIndication
                              } text-center`}
                            >
                              {order.orderType === "INVOICE" ||
                              order.orderType === "REFUND"
                                ? (order.totalTTC - order.paid).toFixed(2) ===
                                  "0.00"
                                  ? "PAID"
                                  : (order.totalTTC - order.paid).toFixed(2)
                                : order.orderType === "DELIVERY" ||
                                  order.orderType === "REFUND_DELIVERY"
                                ? (order.total - order.paid).toFixed(2) ===
                                  "0.00"
                                  ? "PAID"
                                  : (order.total - order.paid).toFixed(2)
                                : "N/A"}
                            </td>
                            <td
                              className={`${
                                order.delStatus === DeliveryStatus.Cancelled ||
                                order.orderType === "DRAFT"
                                  ? styles.naIndication
                                  : order.delStatus === DeliveryStatus.Complete
                                  ? styles.completeIndication
                                  : styles.pendingIndication
                              } text-center`}
                            >
                              {order.delStatus}
                            </td>
                          </tr>
                        ))}
                        {/* TOTAL FOOTER ROW */}
                        <tr className={styles.redHighlight}>
                          <td colSpan="9" className="text-start fw-bold">
                            Total Due : {totalDisplayed.toFixed(2)}€
                          </td>
                        </tr>
                      </>
                    ) : (
                      <tr>
                        <td colSpan="7" className={styles.noData}>
                          No orders found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>

                {/* Pagination Controls */}
                <div className={styles.pagination}>
                  {/* Page Size Selector */}
                  <div className={styles.pageSizeSelector}>
                    <label htmlFor="pageSize">Page Size: </label>
                    <select
                      id="pageSize"
                      value={pageSize}
                      onChange={handlePageSizeChange}
                    >
                      <option value={10}>10</option>
                      <option value={15}>15</option>
                      <option value={20}>20</option>
                    </select>
                  </div>
                  <button
                    onClick={() => handlePageChange(pageNo - 1)}
                    disabled={pageNo === 0}
                  >
                    Previous
                  </button>
                  <span>
                    Page {pageNo + 1} of {totalPages}
                  </span>
                  <button
                    onClick={() => handlePageChange(pageNo + 1)}
                    disabled={
                      !data?.content?.length || totalPages === pageNo + 1
                    }
                  >
                    Next
                  </button>
                </div>
              </>
            )}
          </div>
        </Tab>

        <Tab eventKey="Place order" title="Place order">
          <PlaceOrder onSuccess={() => setKey("Order List")} />
        </Tab>
      </Tabs>
    </div>
  );
};
